import { navigateToHomeSection } from "@/lib/shared";
import { Cpu, PlayCircle } from "lucide-react";

/**
 * Shared template for an individual product page.
 * Layout follows the SERVICIOS wireframe:
 *  1) 2-column split hero: left (eyebrow + product name + large subhead),
 *     right (descriptive body in a gradient rounded box + tech pill).
 *  2) Media placeholder (large rounded dark rectangle — for future video).
 *  3) Vertical list of feature blocks (green left-border, green circle
 *     icon, title, body).
 *  4) "Cómo funciona" section (implementación en 3 pasos).
 *  5) Integraciones grid (Teams / Zoom / Webex / Google Voice).
 *  6) CTA banner.
 */
const ProductPage = ({
  eyebrow,
  headline,
  subhead,
  heroBody,
  category,
  features,
  steps,
  ctaHeadline,
  featuresNote,
  productName,
  baseTech,
  integrations,
}) => {
  const shortName = productName || headline;

  return (
    <div className="cn-product-page" data-testid={`product-page-${category}`}>
      {/* ============ HERO — 2-column split ============ */}
      <section
        className="cn-section cn-product-hero"
        data-testid={`${category}-hero`}
      >
        <div className="cn-hero-bg" aria-hidden="true">
          <span className="cn-hero-overlay" />
          <span className="cn-hero-blob cn-hero-blob-1" />
          <span className="cn-hero-blob cn-hero-blob-2" />
          <span className="cn-hero-blob cn-hero-blob-3" />
          <span className="cn-hero-grid" />
        </div>
        <div className="cn-hero-fade" aria-hidden="true" />
        <div className="cn-product-hero-inner cn-product-hero-split">
          <div className="cn-hero-split-left">
            <span
              className="cn-eyebrow-pill"
              data-reveal
              data-testid={`${category}-eyebrow`}
            >
              {eyebrow}
            </span>
            <h2
              className="cn-product-name"
              data-reveal
              data-testid={`${category}-product-name`}
            >
              {shortName}
            </h2>
            <h1
              className="cn-h1 cn-h1-product cn-product-headline"
              data-reveal
              data-testid={`${category}-headline`}
            >
              {subhead}
            </h1>
            <div className="cn-hero-ctas cn-hero-ctas-left" data-reveal>
              <a
                href="/#contacto"
                onClick={(e) => navigateToHomeSection(e, "contacto")}
                className="cn-btn cn-btn-gradient"
                data-testid={`${category}-cta-demo`}
              >
                Solicitar Evaluación Técnica
              </a>
              <a
                href="/"
                className="cn-btn cn-btn-outline"
                data-testid={`${category}-cta-back`}
              >
                Volver a Inicio
              </a>
            </div>
          </div>
          <div className="cn-hero-split-right" data-reveal>
            <div className="cn-hero-body-card">
              {headline && shortName !== headline && (
                <p
                  className="cn-hero-body-title"
                  data-testid={`${category}-hero-title`}
                >
                  {headline}
                </p>
              )}
              <p
                className="cn-hero-body-text"
                data-testid={`${category}-hero-body`}
              >
                {heroBody}
              </p>
              {baseTech && (
                <span
                  className="cn-tech-pill"
                  data-testid={`${category}-base-tech`}
                >
                  <Cpu size={16} strokeWidth={1.9} aria-hidden="true" />
                  Tecnología base: {baseTech}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ============ MEDIA PLACEHOLDER ============ */}
      <section
        className="cn-section cn-media-section"
        data-testid={`${category}-media-section`}
      >
        <div className="cn-container">
          <div
            className="cn-media-placeholder"
            data-reveal
            role="img"
            aria-label={`Video demo próximamente — ${shortName}`}
            data-testid={`${category}-media-placeholder`}
          >
            <PlayCircle size={56} strokeWidth={1.4} aria-hidden="true" />
            <span className="cn-media-caption">
              Video demo próximamente
            </span>
          </div>
        </div>
      </section>

      {/* ============ FEATURES — vertical stack ============ */}
      <section
        className="cn-section cn-section-floating"
        data-testid={`${category}-features-section`}
      >
        <div className="cn-container">
          <div className="cn-section-head">
            <p className="cn-eyebrow" data-reveal>
              CARACTERÍSTICAS
            </p>
            <h2 className="cn-h2 cn-h2-md" data-reveal>
              Lo que incluye {shortName}
            </h2>
          </div>
          <div
            className="cn-feature-list"
            data-testid={`${category}-features-grid`}
          >
            {features.map((f, i) => {
              const FeatureIcon = f.Icon;
              return (
                <div
                  key={f.title}
                  className="cn-feature-block"
                  data-reveal
                  style={{ "--cn-delay": `${i * 0.1}s` }}
                  data-testid={`${category}-feature-card-${i}`}
                >
                  <span className="cn-feature-block-bar" aria-hidden="true" />
                  <span className="cn-feature-block-icon">
                    <FeatureIcon size={22} strokeWidth={1.9} />
                  </span>
                  <div className="cn-feature-block-body">
                    <h3 className="cn-feature-block-title">{f.title}</h3>
                    <p className="cn-feature-block-desc">{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
          {featuresNote && (
            <p
              className="cn-cc-note"
              data-reveal
              data-testid={`${category}-features-note`}
            >
              {featuresNote}
            </p>
          )}
        </div>
      </section>

      {/* ============ CÓMO FUNCIONA ============ */}
      <section
        className="cn-section cn-section-floating cn-section-floating-pale"
        data-testid={`${category}-como-funciona-section`}
      >
        <div className="cn-container">
          <div className="cn-section-head">
            <p className="cn-eyebrow" data-reveal>
              CÓMO FUNCIONA
            </p>
            <h2 className="cn-h2 cn-h2-md" data-reveal>
              Implementación en 3 pasos
            </h2>
          </div>
          <div className="cn-grid-3">
            {steps.map((step, i) => {
              const title = typeof step === "string" ? step : step.title;
              const desc = typeof step === "string" ? null : step.desc;
              return (
                <div
                  key={title}
                  className="cn-step-card"
                  data-reveal
                  style={{ "--cn-delay": `${i * 0.1}s` }}
                  data-testid={`${category}-step-${i}`}
                >
                  <span className="cn-step-number">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="cn-step-title">{title}</h3>
                  {desc && <p className="cn-step-desc">{desc}</p>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ INTEGRACIONES ============ */}
      {integrations && integrations.length > 0 && (
        <section
          className="cn-section cn-section-floating"
          data-testid={`${category}-integrations-section`}
        >
          <div className="cn-container">
            <div className="cn-section-head">
              <p className="cn-eyebrow" data-reveal>
                INTEGRACIONES
              </p>
              <h2 className="cn-h2 cn-h2-md" data-reveal>
                Compatible con tus plataformas de colaboración
              </h2>
              <p className="cn-section-body" data-reveal>
                {shortName} se conecta de forma nativa con las plataformas
                UCaaS que ya usa tu empresa. Sin cambios de proveedor, sin
                fricción para los usuarios.
              </p>
            </div>
            <div className="cn-integration-grid" data-reveal>
              {integrations.map((name, i) => (
                <div
                  key={name}
                  className="cn-integration-chip"
                  style={{ "--cn-delay": `${i * 0.06}s` }}
                  data-testid={`${category}-integration-${i}`}
                >
                  <span className="cn-integration-dot" aria-hidden="true" />
                  {name}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ CTA BANNER ============ */}
      <section
        className="cn-section cn-section-dark cn-cta-banner"
        data-testid={`${category}-cta-banner`}
      >
        <div className="cn-container cn-cta-inner">
          <h2 className="cn-h2 cn-h2-light" data-reveal>
            {ctaHeadline}
          </h2>
          <a
            href="/#contacto"
            onClick={(e) => navigateToHomeSection(e, "contacto")}
            className="cn-btn cn-btn-gradient cn-btn-lg"
            data-reveal
            data-testid={`${category}-cta-banner-btn`}
          >
            Agendar Sesión de Descubrimiento
          </a>
        </div>
      </section>
    </div>
  );
};

export default ProductPage;
